import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { X } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";

import { Button } from "components/ui/Button";
import { Pill } from "components/ui/Pill";
import { CarePlanForm } from "features/cdm/components/CarePlanForm/CarePlanForm";
import { CdmReviewReferences } from "features/cdm/components/CdmReviewReferences";
import { DiscardFormModal } from "features/cdm/components/DiscardFormModal/DiscardFormModal";
import { ReviewAndExamForm } from "features/cdm/components/ReviewAndExamForm/ReviewAndExamForm";
import { Steps } from "features/cdm/components/Steps/Steps";
import { isRegistration } from "features/cdm/constants/reviewTypes";
import { useGetCarePlan } from "features/cdm/hooks/useGetCarePlan";
import { useLatestReviewId } from "features/cdm/hooks/useLatestReviewId";
import { views } from "features/cdm/hooks/useView";
import { getPpSubmitMessage } from "features/cdm/pp/utils/ppSubmitMessage";
import { buildClinicalPrefill } from "features/cdm/utils/clinicalDataPrefill";
import { extractSubmissionError } from "features/cdm/utils/extractSubmissionError";
import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { usePatientClinicalData } from "hooks/usePatientClinicalData";
import { formatCdmStatus,getCdmStatusColors } from "utils/cdmStatus";

import { PlanAndOutcomeForm } from "./components/PlanAndOutcomeForm/PlanAndOutcomeForm";
import { RiskAndAssessmentForm } from "./components/RiskAndAssessmentForm/RiskAndAssessmentForm";
import { TestAndMedicationForm } from "./components/TestAndMedicationForm/TestAndMedicationForm";
import { useSendPP } from "./hooks/useSendPP";
import { useValidatePP } from "./hooks/useValidatePP";
import style from "./PPItemForm.module.css";

// Tests & Investigations precedes Risk & Assessment so the QRisk3 calculator (in
// Risk & Assessment) can prefill from the bloods entered on the Tests step.
const PP_STEPS = [
  "Review & Exam",
  "Tests & Investigations",
  "Risk & Assessment",
  "Plan & Outcome",
  "Care Plan",
];

export const PPItemForm = ({
  item,
  setView,
  isAnnualReview,
  patientId,
  removeItem,
}) => {
  const [draft, setDraft, removeDraft] = useLocalStorage(
    `kinesin-pp-draft-${patientId}-${item?.id}`,
    { data: item, activeStep: 1 }
  );

  const { data, activeStep } = draft;
  const setData = (updater) =>
    setDraft((prev) => ({
      ...prev,
      data: typeof updater === "function" ? updater(prev.data) : updater,
    }));
  const setActiveStep = (step) =>
    setDraft((prev) => ({ ...prev, activeStep: step }));

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const { data: baselineData } = usePatientBaseline(patientId);
  const { data: clinicalData } = usePatientClinicalData(patientId);
  // New/edited review has no care plan of its own yet — prefill from the
  // patient's latest existing review (previous goals, current medications).
  const latestReviewId = useLatestReviewId(patientId);
  const { data: carePlanView } = useGetCarePlan(latestReviewId);

  const { mutateAsync: validatePP } = useValidatePP({ patientId });
  const { mutateAsync: sendPP, isPending } = useSendPP({
    patientId,
  });

  useEffect(() => {
    if (!item) {
      setView({ name: views.noChronicDiseases, cdmId: null });
    }
  }, [item, setView]);

  useEffect(() => {
    if (!clinicalData) return;

    const { bloods, vitals, diagnostics, lifestyle } =
      buildClinicalPrefill(clinicalData);

    setData((prev) => {
      const merged = {};

      // PP fields are not matrix-gated; prefill any returned value that the
      // user has not already set (a user edit in `prev` always wins).
      for (const group of [bloods, vitals, diagnostics, lifestyle]) {
        for (const [field, value] of Object.entries(group)) {
          if (!prev[field]) merged[field] = value;
        }
      }

      return Object.keys(merged).length > 0 ? { ...prev, ...merged } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalData]);

  if (!item) {
    return null;
  }

  return (
    <section className={style.wrapper}>
      <div className={style.header}>
        <div className={style.headerInner}>
          <h3>{item.name}</h3>
          <p>{format(new Date(item.date), "dd-MM-yyyy")}</p>
        </div>
        <div className={style.statusWrapper}>
          <Pill
            color={getCdmStatusColors(item.status).color}
            bg={getCdmStatusColors(item.status).bg}
            border={getCdmStatusColors(item.status).border}
          >
            {formatCdmStatus(item.status)}
          </Pill>
          <Button
            variant="outlineGray"
            iconButton
            onClick={() => setIsDiscardModalOpen(true)}
            aria-label="Discard form"
          >
            <X size={16} />
          </Button>
        </div>
      </div>
      {isDiscardModalOpen && (
        <DiscardFormModal
          onClose={() => setIsDiscardModalOpen(false)}
          onConfirm={() => {
            removeDraft();
            removeItem();
          }}
        />
      )}
      <Steps steps={PP_STEPS} activeStep={activeStep} />
      <CdmReviewReferences patientId={patientId} />
      {activeStep === 1 && (
        <ReviewAndExamForm
          isPP={true}
          data={data}
          baselineData={baselineData}
          onContinue={(data) => {
            setData((prev) => ({ ...prev, ...data }));
            setActiveStep(activeStep + 1);
          }}
        />
      )}
      {activeStep === 2 && (
        <TestAndMedicationForm
          data={data}
          onBack={() => setActiveStep(activeStep - 1)}
          onContinue={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
          medications={item.medications}
          isAnnualReview={isAnnualReview}
          isRegistration={isRegistration(item?.reviewType)}
        />
      )}
      {activeStep === 3 && (
        <RiskAndAssessmentForm
          data={data}
          isAnnualReview={isAnnualReview}
          onBack={() => setActiveStep(activeStep - 1)}
          onContinue={(data) => {
            setData((prev) => ({ ...prev, ...data }));
            setActiveStep(activeStep + 1);
          }}
        />
      )}
      {activeStep === 4 && (
        <PlanAndOutcomeForm
          data={data}
          onBack={() => setActiveStep(activeStep - 1)}
          onContinue={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
        />
      )}
      {activeStep === 5 && (
        <CarePlanForm
          data={data}
          carePlanView={carePlanView}
          isPp
          isRegistration={isRegistration(item?.reviewType)}
          isLoading={isPending}
          onBack={() => setActiveStep(activeStep - 1)}
          onContinue={async (newData) => {
            const dataToSend = { ...data, ...newData };

            // Validate the full review (clinical data + care plan) only on the
            // final step, right before submission — the care plan goals required
            // by the BE validator are entered here, so an earlier validate (e.g.
            // on the Tests step) always fails on "at least one goal".
            const validation = await validatePP(dataToSend);

            if (!validation.isValid) {
              toast.error(
                validation.validationErrors?.length > 0
                  ? validation.validationErrors.join(", ")
                  : "Something went wrong"
              );

              return;
            }

            try {
              const res = await sendPP(dataToSend);

              if (res?.success !== false) {
                const message = getPpSubmitMessage(res?.outcomeStatus);
                toast.success(message);
                removeDraft();
                removeItem();
              } else {
                toast.error(
                  extractSubmissionError(res, "Error while sending PP")
                );
              }
            } catch (error) {
              console.error("PP submission failed:", error);

              toast.error(
                extractSubmissionError(error, "Error while sending PP")
              );
            }
          }}
        />
      )}
    </section>
  );
};
